import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  isCoarsePointer,
  type TextSelection,
} from '@/hooks/ui/useTextSelection';

export interface SelectionAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: (text: string) => void | Promise<void>;
}

interface SelectionPopoverProps {
  selection: TextSelection | null;
  actions: SelectionAction[];
  onAfterAction?: () => void;
}

const POPOVER_OFFSET = 8;
/** Below a touch selection: clears the drag handles under the last line. */
const TOUCH_OFFSET = 28;
const VIEWPORT_PADDING = 8;

/**
 * Floating menu anchored above a text selection. Pure presentation -
 * pair with `useTextSelection` to drive the `selection` prop.
 *
 * Uses a portal so it never gets clipped by overflow:hidden ancestors,
 * and clamps itself to the viewport so it stays visible near edges.
 */
export function SelectionPopover({
  selection,
  actions,
  onAfterAction,
}: SelectionPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  // Recompute position whenever the selection rect or popover size changes.
  useLayoutEffect(() => {
    if (!selection || !popoverRef.current) {
      setPosition(null);
      return;
    }
    const popover = popoverRef.current;
    const { width: pw, height: ph } = popover.getBoundingClientRect();
    const { top, left, width } = selection.rect;

    let nextTop = top - ph - POPOVER_OFFSET;
    let nextLeft = left + width / 2 - pw / 2;

    // On a touch screen the phone's own Copy / Look Up menu takes the space
    // ABOVE a selection, so ours goes below it, past the drag handles.
    // Otherwise flip below only if there is no room above.
    if (isCoarsePointer()) {
      nextTop = selection.rect.bottom + TOUCH_OFFSET;
    } else if (nextTop < VIEWPORT_PADDING) {
      nextTop = selection.rect.bottom + POPOVER_OFFSET;
    }
    // Clamp horizontally to viewport.
    const maxLeft = window.innerWidth - pw - VIEWPORT_PADDING;
    if (nextLeft < VIEWPORT_PADDING) nextLeft = VIEWPORT_PADDING;
    if (nextLeft > maxLeft) nextLeft = maxLeft;

    setPosition({ top: nextTop + window.scrollY, left: nextLeft });
  }, [selection]);

  // Reset busy state when selection changes.
  useEffect(() => {
    setBusyAction(null);
  }, [selection?.text]);

  if (!selection) return null;

  const handleClick = async (action: SelectionAction) => {
    setBusyAction(action.id);
    try {
      await action.onClick(selection.text);
    } finally {
      setBusyAction(null);
      onAfterAction?.();
    }
  };

  return createPortal(
    <div
      ref={popoverRef}
      data-selection-popover
      // Pre-position offscreen on first render so the layout-effect can
      // measure size without flashing.
      style={
        position
          ? { position: 'absolute', top: position.top, left: position.left }
          : { position: 'absolute', top: -9999, left: -9999 }
      }
      className={cn(
        'z-50 flex items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md',
        'duration-150 animate-in fade-in slide-in-from-bottom-1'
      )}
      // Keep selection alive while user clicks an action button.
      onMouseDown={e => e.preventDefault()}
    >
      {actions.map(action => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            type='button'
            disabled={busyAction !== null}
            onClick={() => handleClick(action)}
            className={cn(
              // Finger-sized below `sm`, the compact desktop size above.
              'inline-flex items-center gap-1.5 rounded-sm px-3 py-2.5 text-sm font-medium text-foreground/80 sm:px-2 sm:py-1 sm:text-xs',
              'hover:bg-muted hover:text-foreground',
              'disabled:cursor-not-allowed disabled:opacity-60'
            )}
          >
            {Icon ? <Icon className='h-3.5 w-3.5' /> : null}
            {action.label}
          </button>
        );
      })}
    </div>,
    document.body
  );
}
